import { memo, useMemo, useState } from 'react';

import { Button, Divider, InputRadio } from '@/components';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import { voteSurvey } from '@/services/dashboard';
import { OpcionEncuestaTO } from '@/types';
import { formatearNumero, TEXTS } from '@/utils';

export const SurveySection = memo(function SurveySection() {
  const toast = useToast();
  const { isDesktop, isMobile } = useIsDesktop();
  const { userInitialData, setUserInitialData } = useAuth();
  const [selectedOption, setSelectedOption] = useState<number | null>(null);

  const handleRadioChange = (name: string, value: string | number) => {
    setSelectedOption(typeof value === 'number' ? value : null);
  };

  const handleSubmit = async () => {
    if (selectedOption && userInitialData) {
      try {
        const responseSurveySubmit = await voteSurvey(selectedOption);
        if (responseSurveySubmit.estado) {
          setUserInitialData({
            ...userInitialData,
            puedeVotar: false,
          });
        } else {
          toast.show(TEXTS.defaultError);
        }
      } catch (error) {
        console.error(error);
        toast.show(TEXTS.defaultError);
      }
    }
  };

  const totalVotesCount = useMemo(() => {
    if (userInitialData?.encuesta?.opciones) {
      return userInitialData.encuesta.opciones.reduce(
        (total, objeto) => total + (objeto.totalVotos ?? 0),
        0
      );
    }
    return 0;
  }, [userInitialData?.encuesta?.opciones]);

  const getVotersText = () => {
    return `${formatearNumero(totalVotesCount)} ${
      totalVotesCount === 1 ? 'VOTANTE' : 'VOTANTES'
    }`;
  };
  const getHighestVotedItemId = () => {
    if (userInitialData?.encuesta?.opciones) {
      const highestVotedItem = userInitialData.encuesta.opciones.reduce(
        (max, item) =>
          (item.totalVotos ?? 0) > (max.totalVotos ?? 0) ? item : max,
        userInitialData.encuesta.opciones[0]
      );
      return highestVotedItem?.id ?? null;
    }
    return null;
  };
  const highestVotedItemId = getHighestVotedItemId();

  const calculateProportionalPercentages = () => {
    if (totalVotesCount === 0) return [];

    const options = userInitialData?.encuesta?.opciones || [];

    // Calcular porcentajes iniciales
    const initialPercentages = options.map(
      (opt) => ((opt.totalVotos ?? 0) / totalVotesCount) * 100
    );

    // Redondear porcentajes
    const roundedPercentages = initialPercentages.map((p) => Math.round(p));

    // Calcular la diferencia
    const totalRounded = roundedPercentages.reduce((a, b) => a + b, 0);
    const difference = 100 - totalRounded;

    // Ajustar el último elemento o distribuir la diferencia
    if (difference !== 0) {
      // Encontrar el índice del elemento con el porcentaje más cercano para ajustar
      const indexToAdjust = initialPercentages.reduce(
        (maxIndex, current, index) =>
          Math.abs(current - roundedPercentages[index]) >
          Math.abs(current - roundedPercentages[maxIndex])
            ? index
            : maxIndex,
        0
      );

      roundedPercentages[indexToAdjust] += difference;
    }

    return roundedPercentages;
  };

  const renderListElement = (item: OpcionEncuestaTO, index: number) => {
    const percentages = calculateProportionalPercentages();

    const votePercentage = percentages[index] ?? 0;

    if (userInitialData && userInitialData.puedeVotar) {
      return (
        <InputRadio.Input
          key={item.id}
          className="my-0.5"
          isChecked={selectedOption === item.id}
          label={item.descripcion || ''}
          name={item.descripcion ?? ''}
          value={item.id ?? 0}
          onChange={handleRadioChange}
        />
      );
    } else {
      return (
        <div key={item.id} className="flex flex-col items-start mb-2">
          <span
            className={`text-left text-xs mb-1 ${
              item.id === highestVotedItemId ? 'font-bold' : 'font-light'
            }`}
          >
            {item.descripcion}
          </span>
          <div className="flex w-full items-center">
            <p className="text-left text-gdt-blue text-xs">{votePercentage}%</p>
            <div className="w-[90%] bg-gray-200 rounded-full h-1 mb-1 ">
              <div
                className="bg-gdt-blue h-1 rounded-full mb-1"
                style={{ width: `${votePercentage}%` }}
              />
            </div>
          </div>
        </div>
      );
    }
  };

  const encuesta = userInitialData?.encuesta;

  return (
    <>
      {isDesktop && (
        <div className="bg-white h-[359px] w-full flex flex-col text-base font-bold rounded-lg">
          <div className="flex justify-between px-8 pt-7">
            <p className="text-sm text-gdt-gray-3 font-semibold mb-1">
              ENCUESTA
            </p>
            {!!encuesta && (
              <p className="text-sm text-gdt-gray-3 font-normal mb-1">
                {getVotersText()}
              </p>
            )}
          </div>
          {!!encuesta && (
            <>
              <p className="text-xs xl:text-sm text-black font-semibold px-8">
                {encuesta.consigna}
              </p>
              <Divider className="my-3" type="horizontal" />
              <div className="mx-8 flex-1 items-start justify-start content-start overflow-auto lg:max-h-[426px] pb-[92px] lg:pb-5">
                {encuesta.opciones?.map((item, index) =>
                  renderListElement(item, index)
                )}
              </div>
              {userInitialData && (
                <Button
                  className="h-[2.25rem] my-4  lg:w-[8.4rem] px-2 py-0 text-xs lg:text-sm self-center"
                  disabled={!selectedOption || !userInitialData.puedeVotar}
                  type="button"
                  variant={'primary'}
                  onClick={handleSubmit}
                >
                  Votar
                </Button>
              )}
            </>
          )}
        </div>
      )}
      {isMobile && (
        <>
          <div className="flex flex-row items-center gap-4">
            <div className="p-3  bg-gdt-status-yellow-light rounded-full text-2xl text-gdt-status-yellow-icon px-4">
              ?
            </div>
            <p className="mb-[6px] font-bold text-zinc-900 text-lg pl-1">
              Encuesta
            </p>
          </div>
          <div className="bg-white h-[359px] w-full flex flex-col text-base font-bold rounded-lg">
            {!!encuesta && (
              <>
                <div className="flex justify-between px-8 pt-8">
                  <p className="text-sm text-gdt-gray-3 font-normal mb-4">
                    {getVotersText()}
                  </p>
                </div>
                <p className="text-xs text-black font-semibold px-8">
                  {encuesta.consigna}
                </p>
                <Divider className="my-2" type="horizontal" />
                <div
                  className="mx-8 flex-1 items-start justify-start content-start overflow-y-scroll  max-h-[300px]"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#3366ff ',
                  }}
                >
                  {encuesta.opciones?.map((item, index) =>
                    renderListElement(item, index)
                  )}
                </div>
                {userInitialData && (
                  <Button
                    className="h-[2.25rem] my-4 lg:h-[2.6rem] lg:w-[8.4rem] px-8 py-0 text-xs lg:text-sm self-center"
                    disabled={!selectedOption || !userInitialData.puedeVotar}
                    type="button"
                    variant={'primary'}
                    onClick={handleSubmit}
                  >
                    Votar
                  </Button>
                )}
              </>
            )}
          </div>
        </>
      )}
    </>
  );
});
